import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider, useData } from './contexts/DataContext';
import { T } from './utils/theme';
import { Logo } from './components/ui';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import TrocaSenhaPage from './pages/TrocaSenhaPage';
import KioskApp from './components/kiosk/KioskApp';
import MasterDashboard from './components/MasterDashboard';
import pushService from './services/pushService';

// ── Tab pages: eager imports — always in memory, zero load time ──
import Dashboard from './pages/Dashboard';
import FilaPage from './pages/FilaPage';
import ComandasPage from './pages/ComandasPage';
import GestaoSaquesPage from './pages/GestaoSaquesPage';
import MinhasComissoesPage from './pages/MinhasComissoesPage';

// ── Secondary pages: lazy + prefetch immediately ──
const AgendamentosPage = React.lazy(() => import('./pages/AgendamentosPage'));
const ServicosPage = React.lazy(() => import('./pages/ServicosPage'));
const BarbeirosPage = React.lazy(() => import('./pages/BarbeirosPage'));
const UsuariosPage = React.lazy(() => import('./pages/UsuariosPage'));
const RelatoriosPage = React.lazy(() => import('./pages/RelatoriosPage'));
const FaturamentoPage = React.lazy(() => import('./pages/FaturamentoPage'));
const FinanceiroPage = React.lazy(() => import('./pages/FinanceiroPage'));
const DisponibilidadePage = React.lazy(() => import('./pages/DisponibilidadePage'));
const WhatsappPage = React.lazy(() => import('./pages/WhatsappPage'));
const FiliaisPage = React.lazy(() => import('./components/MultiLojas/FiliaisPage'));
const GestaoEquipePage = React.lazy(() => import('./components/MultiLojas/GestaoEquipePage'));

// Prefetch ALL secondary chunks immediately on app start
setTimeout(() => {
  import('./pages/AgendamentosPage');
  import('./pages/ServicosPage');
  import('./pages/BarbeirosPage');
  import('./pages/UsuariosPage');
  import('./pages/RelatoriosPage');
  import('./pages/FaturamentoPage');
  import('./pages/FinanceiroPage');
  import('./pages/DisponibilidadePage');
}, 100);

const KNOWN_PATHS = ['/', '/fila', '/comandas', '/nova-comanda', '/agenda', '/servicos', '/barbeiros', '/usuarios', '/relatorios', '/faturamento', '/gestao-saques', '/financeiro', '/comissoes', '/disponibilidade', '/whatsapp', '/multi-filiais', '/multi-equipe'];

const CAMINHOS_BARBEIRO = ['/', '/fila', '/comandas', '/nova-comanda', '/agenda', '/comissoes', '/disponibilidade'];

const SuspendedGuard = React.memo(({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user?.barbeariaSuspended) navigate('/faturamento', { replace: true });
  }, [user?.barbeariaSuspended, navigate]);
  if (user?.barbeariaSuspended) return null;
  return children;
});

const LAZY_FALLBACK = <div style={{ minHeight: '60vh' }} />;

const AppRoutes = () => {
  const { user, logout, validatingToken } = useAuth();
  const { carregarDados, limparDados } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedFilialId, setSelectedFilialId] = useState(null);

  const path = location.pathname;

  useEffect(() => {
    if (!KNOWN_PATHS.includes(path)) navigate('/', { replace: true });
  }, [path, navigate]);

  useEffect(() => {
    if (!user) limparDados();
  }, [user, limparDados]);

  // Barbeiro não acessa telas de gestão pela URL
  useEffect(() => {
    if (user?.tipo === 'barbeiro' && !CAMINHOS_BARBEIRO.includes(path)) navigate('/', { replace: true });
  }, [user?.tipo, path, navigate]);

  useEffect(() => {
    if (validatingToken) return;
    if (user && user.tipo !== 'master' && !user.trocarSenha) carregarDados();
  }, [user, carregarDados, validatingToken]);

  useEffect(() => {
    if (user) {
      setTimeout(async () => {
        try { await pushService.initialize(); } catch {}
      }, 1000);
    }
  }, [user]);

  useEffect(() => {
    if (user?.barbeariaSuspended) navigate('/faturamento', { replace: true });
  }, [user?.barbeariaSuspended, navigate]);

  // Memoize keep-alive pages — prevents re-renders on navigation
  const dashboardEl = useMemo(() => <SuspendedGuard><Dashboard /></SuspendedGuard>, []);
  const filaEl = useMemo(() => <SuspendedGuard><FilaPage /></SuspendedGuard>, []);
  const comandasEl = useMemo(() => <SuspendedGuard><ComandasPage /></SuspendedGuard>, []);
  const gestaoSaquesEl = useMemo(() => <SuspendedGuard><GestaoSaquesPage /></SuspendedGuard>, []);
  const comissoesEl = useMemo(() => <SuspendedGuard><MinhasComissoesPage /></SuspendedGuard>, []);

  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: T.bg }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><Logo className="w-16 h-16" /></div>
          <p style={{ color: T.label, fontSize: 18 }}>Validando sessao...</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;

  if (user.trocarSenha) return <TrocaSenhaPage />;

  if (user.tipo === 'master') return <MasterDashboard user={user} onLogout={logout} />;

  const handleGerenciarEquipeFilial = (filialId) => {
    setSelectedFilialId(filialId);
    navigate('/multi-equipe');
  };

  const handleAccessFilial = (filialId) => {
    try {
      localStorage.setItem('barbx_selected_barbearia', filialId);
      window.dispatchEvent(new CustomEvent('barbx_store_changed', { detail: { storeId: filialId } }));
    } catch {}
    navigate('/');
    setTimeout(() => carregarDados(true), 100);
  };

  let selectedStoreId = null;
  try { selectedStoreId = localStorage.getItem('barbx_selected_barbearia'); } catch {}
  const matrizId = user.barbeariaId || selectedStoreId || null;

  return (
    <Layout>
      {/* ── Keep-alive tab pages: ALL pre-mounted, hidden with display:none ── */}
      <div style={{ display: path === '/' ? '' : 'none', contain: 'style paint' }}>{dashboardEl}</div>
      <div style={{ display: path === '/fila' ? '' : 'none', contain: 'style paint' }}>{filaEl}</div>
      <div style={{ display: path === '/comandas' ? '' : 'none' }}>{comandasEl}</div>
      <div style={{ display: path === '/gestao-saques' ? '' : 'none', contain: 'style paint' }}>{gestaoSaquesEl}</div>
      <div style={{ display: path === '/comissoes' ? '' : 'none', contain: 'style paint' }}>{comissoesEl}</div>

      {/* ── On-demand pages ── */}
      {path === '/nova-comanda' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><ComandasPage abrirModalDireto onNovaComandaConsumida={() => {}} /></SuspendedGuard></Suspense>}
      {path === '/agenda' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><AgendamentosPage /></SuspendedGuard></Suspense>}
      {path === '/servicos' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><ServicosPage /></SuspendedGuard></Suspense>}
      {path === '/barbeiros' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><BarbeirosPage /></SuspendedGuard></Suspense>}
      {path === '/usuarios' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><UsuariosPage /></SuspendedGuard></Suspense>}
      {path === '/relatorios' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><RelatoriosPage /></SuspendedGuard></Suspense>}
      {path === '/faturamento' && <Suspense fallback={LAZY_FALLBACK}><FaturamentoPage /></Suspense>}
      {path === '/financeiro' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><FinanceiroPage /></SuspendedGuard></Suspense>}
      {path === '/disponibilidade' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><DisponibilidadePage /></SuspendedGuard></Suspense>}
      {path === '/whatsapp' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard><WhatsappPage /></SuspendedGuard></Suspense>}
      {path === '/multi-filiais' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard>{matrizId ? <FiliaisPage matrizId={matrizId} onGerenciarEquipe={handleGerenciarEquipeFilial} onAccessFilial={handleAccessFilial} /> : <Dashboard />}</SuspendedGuard></Suspense>}
      {path === '/multi-equipe' && <Suspense fallback={LAZY_FALLBACK}><SuspendedGuard>{selectedFilialId ? <GestaoEquipePage filialId={selectedFilialId} /> : null}</SuspendedGuard></Suspense>}
    </Layout>
  );
};

const App = () => {
  const [usuarios, setUsuarios] = useState([]);

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/kiosk')) {
    return <KioskApp />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <DataProvider usuariosState={[usuarios, setUsuarios]}>
          <AppRoutes />
        </DataProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
